import {
  Box,
  Button,
  Stack,
  TextareaAutosize,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Control, Controller, FieldValues, useForm, useWatch } from 'react-hook-form'
import CardTAG from '../../CardTAG'
import DividerTAG from '../../DividerTAG'
import UploadDropzone from '../../UploadDropzone'
import FormLayout from '../FormLayout'
import { IAddMaterialForm } from './types'
import colors from '../../../constants/colors'
import uploadConstants from '../../UploadDropzone/constants'

import { ReactComponent as TextIcon } from '../../../assets/images/TextIcon.svg'
import { ReactComponent as ImageIcon } from '../../../assets/images/ImageIcon.svg'
import { ReactComponent as CameraIcon } from '../../../assets/images/CameraIconSmall.svg'
import { ReactComponent as AudioIcon } from '../../../assets/images/AudioIcon.svg'
import { ReactComponent as DocumentIcon } from '../../../assets/images/DocumentIcon.svg'
import { ReactComponent as LinkIcon } from '../../../assets/images/LinkIcon.svg'
import React, { useRef, useState } from 'react'
import constants from './constants'
import { uploadInputZones } from '../../UploadDropzone/types'
import AddIcon from '@mui/icons-material/Add'
import utils from '../../CaseInvestigation/utils'
// import { IPOSTMaterial } from '../../../store/materials/types'
import { textAreaStyle } from '../../../constants/styleConstants'

const DropzoneField = ({
  name,
  control,
  currentValues,
  iconAndText,
  acceptType = uploadInputZones.ALL,
}: {
  name: string
  control: Control<FieldValues>
  currentValues: any[]
  iconAndText?: React.ReactNode
  acceptType?: uploadInputZones
}) => {
  return (
    <Controller
      render={({ field }) => {
        return (
          <>
            <UploadDropzone
              onChange={(e: any) => {
                return field.onChange([...currentValues, ...e])
              }}
              iconAndText={iconAndText}
              acceptType={acceptType}
            />
          </>
        )
      }}
      name={name}
      control={control}
      defaultValue=''
    />
  )
}

const AddMaterialForm: React.FC<IAddMaterialForm> = ({
  title = 'Add New Material',
  control,
  fieldName,
  closeModal,
  color = 'tagBoxSuccess',
  setValue,
}) => {
  const [selectedItem, setSelectedItem] = useState('')
  const titleFieldRef = useRef<HTMLInputElement>(null)
  const descriptionFieldRef = useRef<HTMLTextAreaElement>(null)
  const linkFieldRef = useRef<HTMLInputElement>(null)
  // const [inputFiles, setInputFiles] = useState<IPOSTMaterial[]>([])
  const formMethods = useForm<{
    [key: string]: string | any
  }>({
    defaultValues: { title: '', type: '', description: '', files: [] },
    mode: 'onChange',
    shouldFocusError: false,
  })
  const { control: controlLocal, setValue: setValueLocal, reset } = formMethods

  const existingFiles = useWatch({
    control: control,
    name: `${fieldName}`, // without supply name will watch the entire form, or ['firstName', 'lastName'] to watch both
    defaultValue: '', // default value before the render
  })

  const selectedFiles = useWatch({
    control: controlLocal,
    name: `${fieldName}.attachments`, // without supply name will watch the entire form, or ['firstName', 'lastName'] to watch both
    defaultValue: '', // default value before the render
  })
  
  const handleSelectChange = (
    event: React.MouseEvent<HTMLElement>,
    newSelectedItem: string | null,
  ) => {
    if (newSelectedItem !== null) {
      setSelectedItem(newSelectedItem)
    }
  }

  const addTextOrLinkFieldMaterial = () => {
    const title = titleFieldRef.current?.value
    const description = descriptionFieldRef.current?.value
    const link = linkFieldRef?.current?.value
    const formatedMaterial = {
      title,
      description,
      type: selectedItem,
      link,
    }
    setValue(`${fieldName}`, [...existingFiles, formatedMaterial], { shouldDirty: true })
    setSelectedItem('')
    reset()
  }

  const addMaterial = () => {
    const title = titleFieldRef.current?.value
    const description = descriptionFieldRef.current?.value
    const link = linkFieldRef.current?.value
    const formatedMaterial = {
      title,
      description,
      link,
      type: selectedItem,
      attachments: [...selectedFiles],
    }

    setValue(`${fieldName}`, [...existingFiles, formatedMaterial], { shouldDirty: true })
    setSelectedItem('')
    reset()
  }

  const deleteAdditionalMaterials = (identifier: any) => {
    //const newFiles = selectedFiles.filter((x: { name: string }) => x?.name !== identifier)
    // eslint-disable-next-line
    // console.log('Identifier', identifier, selectedFiles)
    //setValue(fieldName, [...existingFiles, newFiles], { shouldDirty: true })
  }

  const renderAdditionalMaterials = () => {
    const title = titleFieldRef.current?.value
    const description = descriptionFieldRef.current?.value
    const formatedMaterial = {
      title,
      description,
      type: selectedItem as uploadInputZones,
      attachments: [
        ...selectedFiles.map((file: File) => {
          return {
            link: URL.createObjectURL(file),
            name: file?.name,
            size: file?.size,
          }
        }),
      ],
    }

    if (selectedFiles?.length !== 0) {
      const whatToRender = utils.renderUploadedFiles(
        [formatedMaterial],
        deleteAdditionalMaterials,
        false,
      )
      return whatToRender
    }

    return <></>
  }

  const resolveRenderBasedOnSelectedButton = () => {
    if (selectedItem === '') {
      return <></>
    }

    if (selectedItem === uploadInputZones.TEXT_FIELD || selectedItem === uploadInputZones.LINK) {
      return (
        <>
          <DividerTAG />
          <Box>
            <FormLayout>
              <TextField
                inputRef={titleFieldRef}
                variant='standard'
                type={'text'}
                fullWidth
                placeholder={'Title (Required)'}
              />
              {selectedItem === uploadInputZones.TEXT_FIELD && (
                <TextareaAutosize
                  ref={descriptionFieldRef}
                  style={textAreaStyle}
                  minRows={5}
                  placeholder={'Description (Required)'}
                />
              )}
              {selectedItem === uploadInputZones.LINK && (
                <TextField
                  inputRef={linkFieldRef}
                  variant='standard'
                  type={'text'}
                  fullWidth
                  placeholder={'Link:'}
                />
              )}
              <Button
                variant='outlined'
                startIcon={<AddIcon />}
                sx={{ color: colors.BLACK }}
                onClick={addTextOrLinkFieldMaterial}
              >
                <Typography variant='bodyRegular' color='inherit'>
                  Add Material
                </Typography>
              </Button>
            </FormLayout>
          </Box>
        </>
      )
    }

    return (
      <>
        <DividerTAG />
        <FormLayout>
          <DropzoneField
            control={controlLocal}
            name={`${fieldName}.attachments`}
            currentValues={selectedFiles}
            iconAndText={uploadConstants.getDropzoneIconsAndText({ type: selectedItem })}
            acceptType={uploadConstants.dropzoneAcceptanceType[selectedItem]}
          />
          <TextField
            inputRef={titleFieldRef}
            variant='standard'
            type={'text'}
            fullWidth
            placeholder={'Title (Required)'}
          />
          <TextareaAutosize
            ref={descriptionFieldRef}
            style={textAreaStyle}
            minRows={5}
            placeholder={'Description (Required)'}
          />
          {selectedFiles.length !== 0 && <FormLayout>{renderAdditionalMaterials()}</FormLayout>}
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            sx={{ color: colors.BLACK }}
            onClick={addMaterial}
          >
            <Typography variant='bodyRegular' color='inherit'>
              Add Material
            </Typography>
          </Button>
        </FormLayout>
      </>
    )
  }
  return (
    <CardTAG title={title} closeCard={closeModal} color={color}>
      <FormLayout>
        <Stack sx={{ width: '100%' }}>
          <Box sx={{ p: '2rem' }}>
            <ToggleButtonGroup
              exclusive
              value={selectedItem}
              onChange={handleSelectChange}
              sx={[constants.buttonsGroupStyle]}
            >
              <ToggleButton
                value={uploadInputZones.TEXT_FIELD}
                sx={[constants.buttonsGroupButton, constants.buttonBorderRight]}
              >
                <TextIcon fill={colors.TAG_PURPLE} />
                <Typography variant='bodyBold'>Text Field</Typography>
              </ToggleButton>
              <ToggleButton
                value={uploadInputZones.IMAGE}
                sx={[constants.buttonsGroupButton, constants.buttonBorderRight]}
              >
                <ImageIcon fill={colors.TAG_PURPLE} />
                <Typography variant='bodyBold'>Image</Typography>
              </ToggleButton>
              <ToggleButton value={uploadInputZones.VIDEO} sx={[constants.buttonsGroupButton]}>
                <CameraIcon fill={colors.TAG_PURPLE} />
                <Typography variant='bodyBold'>Video</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <DividerTAG />
          <Box sx={{ p: '2rem' }}>
            <ToggleButtonGroup
              exclusive
              value={selectedItem}
              onChange={handleSelectChange}
              sx={[constants.buttonsGroupStyle]}
            >
              <ToggleButton
                value={uploadInputZones.AUDIO}
                sx={[constants.buttonsGroupButton, constants.buttonBorderRight]}
              >
                <AudioIcon fill={colors.TAG_PURPLE} />
                <Typography variant='bodyBold'>Audio</Typography>
              </ToggleButton>
              <ToggleButton
                value={uploadInputZones.DOCUMENT}
                sx={[constants.buttonsGroupButton, constants.buttonBorderRight]}
              >
                <DocumentIcon fill={colors.TAG_PURPLE} />
                <Typography variant='bodyBold'>Document</Typography>
              </ToggleButton>
              <ToggleButton value={uploadInputZones.LINK} sx={[constants.buttonsGroupButton]}>
                <LinkIcon fill={colors.TAG_PURPLE} />
                <Typography variant='bodyBold'>Link</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Stack>
        {resolveRenderBasedOnSelectedButton()}
      </FormLayout>
    </CardTAG>
  )
}

export default AddMaterialForm
